import React, { createContext, useContext, useCallback, useState, useEffect, useMemo } from "react";
import { InteractionRequiredAuthError, BrowserAuthError } from "@azure/msal-browser";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { notification } from "antd";
import { CloseCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { loginRequest } from "../msalAuth/msalConfig";
import Loader from "../configure/Loader";
import { logInfo, logError } from "../configure/utils";
import axios from "axios";
import { acquireLock, releaseLock } from "../configure/concurrencyLock";
import { updateLastActive } from "../idle/useIdleTimer";
import { msalInstance } from "../../index";

// const msalInstance = new PublicClientApplication(msalConfig);
const SANDEE_SILENT_RENEW_LOCK = "SANDEE_SILENT_RENEW_LOCK";
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [isMsalInitialized, setIsMsalInitialized] = useState(false);

  useEffect(() => {
    async function initializeMsal() {
      try {
        // await msalInstance.initialize();
        const response = await msalInstance.handleRedirectPromise();
        if (response && response.account) {
          msalInstance.setActiveAccount(response.account);
          updateLastActive(true);
        } else {
          const currentAccounts = msalInstance.getAllAccounts();
          if (!msalInstance.getActiveAccount() && currentAccounts.length > 0) {
            msalInstance.setActiveAccount(currentAccounts[0]);
            updateLastActive(true);
          }
        }
        setIsMsalInitialized(true);
      } catch (error) {
        logError("MSAL initialization error:", error);
        if (error instanceof InteractionRequiredAuthError) {
          await msalInstance.loginRedirect({ ...loginRequest, prompt: "select_account" });
        } else if (error instanceof BrowserAuthError) {
          setIsMsalInitialized(true);
          throw new Error("Browser authentication error occurred. Please check your settings.");
        } else {
          setIsMsalInitialized(true);
          throw new Error("Failed to initialize authentication. Please refresh the page.");
        }
      }
    }
    initializeMsal();
  }, []);

  if (!isMsalInitialized) {
    return <Loader message="Initialize..." />;
  }

  return (
    <MsalProvider instance={msalInstance}>
      <AuthContextProvider>{children}</AuthContextProvider>
    </MsalProvider>
  );
}

function AuthContextProvider({ children }) {
  const { instance } = useMsal();
  // const [authStatus, setAuthStatus] = useState(() => !!instance.getActiveAccount());

  const getTokenExpired = useCallback(() => {
    const account = instance.getActiveAccount();
    const idTokenClaims = account?.idTokenClaims;
    if (!idTokenClaims?.exp) {
      return null;
    }
    const expirationTimeMs = idTokenClaims.exp * 1000;
    const date = new Date(expirationTimeMs);
    return date.toLocaleString("th-TH", { dateStyle: "long", timeStyle: "medium" });
  }, [instance]);

  const login = useCallback(async () => {
    try {
      // logInfo("User initiating login redirect...");
      await instance.loginRedirect({ ...loginRequest, prompt: "select_account" });
    } catch (error) {
      logError("Login error:", error);
      let description = "Failed to log in. Please try again.";
      if (error instanceof BrowserAuthError) {
        description = "Please enable cookies and try again.";
      }
      notification.error({
        message: error instanceof BrowserAuthError ? "Browser Settings Error" : "Login Failed",
        description,
        icon: <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />,
      });
    }
  }, [instance]);

  const loginNoPrompt = useCallback(async () => {
    await instance.loginRedirect({ ...loginRequest, prompt: "none" });
  }, [instance]);

  const logout = useCallback(async () => {
    try {
      // logInfo("User initiating logout redirect...");
      await instance.logoutRedirect({
        postLogoutRedirectUri: `${window.location.origin}/sign-in`,
      });
    } catch (error) {
      logError("Logout error:", error);
      notification.error({
        message: "Logout Error",
        description: "Failed to log out properly. Please try again.",
        icon: <CloseCircleOutlined style={{ color: "#ff4d4f" }} />,
      });
    }
  }, [instance]);

  const logoutWithNoRedirect = useCallback(async () => {
    try {
      // await instance.logoutRedirect({
      //   onRedirectNavigate: () => false,
      // });
      await instance.logoutRedirect();
    } catch (error) {
      logError("Logout error:", error);
      notification.error({
        message: "Logout Error",
        description: "Failed to log out properly. Please try again.",
        icon: <CloseCircleOutlined style={{ color: "#ff4d4f" }} />,
      });
    }
  }, [instance]);

  const doSilentRenew = useCallback(async () => {
    try {
      const gotLock = acquireLock(SANDEE_SILENT_RENEW_LOCK);
      if (!gotLock) {
        logInfo("Silent renew already in progress in another tab, skipping here.");
        return;
      }
      const activeAccount = instance.getActiveAccount();
      if (!activeAccount) {
        logInfo("No active account for silent renew; skipping");
        return;
      }
      // logInfo("Attempting silent token renew for active account...");
      await instance.acquireTokenSilent({
        account: activeAccount,
        scopes: loginRequest.scopes,
      });
    } catch (error) {
      logError("Silent renew failed:", error);
      notification.error({
        message: "Silent Renew Failed",
        description: "Could not refresh your token. Please try again or re-login.",
      });
      await login();
    } finally {
      releaseLock(SANDEE_SILENT_RENEW_LOCK);
    }
  }, [instance, login]);

  useEffect(() => {
    // const requestInterceptor = axios.interceptors.request.use((config) => config);
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error?.response?.status === 401) {
          await doSilentRenew();
        }
        return Promise.reject(error);
      }
    );
    return () => {
      // axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [instance, doSilentRenew, logout]);

  const authContextValue = useMemo(
    () => ({
      isAuthenticated: !!instance.getActiveAccount(),
      user: instance.getActiveAccount(),
    }),
    [instance]
  );

  const authActions = useMemo(
    () => ({
      login,
      loginNoPrompt,
      logout,
      logoutWithNoRedirect,
      getTokenExpired,
      doSilentRenew,
    }),
    [login, loginNoPrompt, logout, logoutWithNoRedirect, getTokenExpired, doSilentRenew]
  );

  const contextValue = useMemo(
    () => ({
      ...authContextValue,
      ...authActions,
    }),
    [authContextValue, authActions]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export default AuthProvider;
